import {
  Avatar,
  Clickable,
  Col,
  Logo,
  Modal,
  PostsGrid,
  Row,
  ShoutoutsRow,
  Spinner2,
  TermsAgreement,
  Text
} from 'components';
import { getRoleBgColor } from 'core';
import { canManagePollChannel, getPollRole, theme } from 'lib';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CallToActionLogin, SignupWithEmail, SignupWithSocials } from './Auth';
import QuickQuizRow from './QuickQuiz';
import PollsRow from './Polls';
import TeamChannelSurface from './TeamChannelSurface';

const AUTH_STEPS = {
  SIGN_UP_WITH_SOCIALS: 'SIGN_UP_WITH_SOCIALS',
  SIGN_UP_WITH_EMAIL: 'SIGN_UP_WITH_EMAIL'
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  background-color: rgba(210, 216, 223, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
`;

const ModalContent = styled.div`
  background-color: #fff;
  padding: 24px;
  border-radius: 8px;
  max-width: 500px; // Increase max-width for desktop
  width: 100%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  position: relative; // To position the close button
`;

const LoadingOverlay = () => {
  return (
    <Container>
      <Spinner2 col={theme.SECONDARY} />
    </Container>
  );
};

const Fan = ({ user, contentLocked, otherUser, setOtherUser }) => {
  const isMobile = useSelector((state) => state.appState.isMobile);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user);
  const [loadingSocialSignIn, setLoadingSocialSignIn] = useState(false);

  const [currentAuthStep, setCurrentAuthStep] = useState(AUTH_STEPS.SIGN_UP_WITH_SOCIALS);
  const [loading, setLoading] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);

  const isOtherUserAthlete = otherUser?.type === 'athlete';
  const isOtherUserFan = otherUser?.type === 'fan';

  function handleLoading() {
    setLoading((prevState) => !prevState);
  }

  function handleUnlock() {
    navigate(`/subscribe/${user._id}`);
  }

  function handleLoadingSocialSignIn() {
    setLoadingSocialSignIn((prevState) => !prevState);
  }
  const callToAction = {
    fromUser: {
      action: () => {}
    }
  };
  const followUpDetails = {
    actionType: 'fromUser',
    otherUser,
    action: callToAction['fromUser']?.action
  };

  const renderSignupFooter = () => {
    return (
      <>
        <TermsAgreement maxWid={isMobile ? '100%' : '70%'} />

        <Clickable
          marg="20px 0 12px 0"
          onClick={() => {
            setCurrentAuthStep(AUTH_STEPS.CALL_TO_ACTION_LOGIN);
          }}
        >
          <Text b2>
            {t('auth.alreadyHaveAccount')}{' '}
            <Text spanned b2 med>
              {t('auth.logIn')}
            </Text>
          </Text>
        </Clickable>
      </>
    );
  };

  const renderCurrentStep = () => {
    switch (currentAuthStep) {
      case AUTH_STEPS.SIGN_UP_WITH_SOCIALS:
        return (
          <>
            <SignupWithSocials
              isMobile={isMobile}
              handleLoading={handleLoading}
              onHandleSignUpWithEmail={() => setCurrentAuthStep(AUTH_STEPS.SIGN_UP_WITH_EMAIL)}
              followUpDetails={followUpDetails}
            />

            {renderSignupFooter()}
          </>
        );
      case AUTH_STEPS.SIGN_UP_WITH_EMAIL:
        return (
          <>
            <SignupWithEmail
              otherUser={otherUser}
              actionType={'fromUser'}
              onHandleGoBack={() => setCurrentAuthStep(AUTH_STEPS.SIGN_UP_WITH_SOCIALS)}
              followAthlete={null}
            />
            {renderSignupFooter()}
          </>
        );
      case AUTH_STEPS.CALL_TO_ACTION_LOGIN:
        return (
          <CallToActionLogin
            otherUser={otherUser}
            actionType={'fromUser'}
            setCurrentAuthStep={setCurrentAuthStep}
            followAthlete={null}
            authSteps={AUTH_STEPS}
            followUpDetails={followUpDetails}
            handleLoadingSocialSignIn={handleLoadingSocialSignIn}
          />
        );
      default:
        return (
          <SignupWithSocials
            isMobile={isMobile}
            handleLoading={handleLoading}
            navigate={navigate}
            followUpDetails={null}
          />
        );
    }
  };

  const renderSignUp = () => {
    return (
      <Col centerAll wid="100%" minWid={!isMobile ? '500px' : '100%'}>
        {/* User Profile */}
        <Col wid="100%" centerAll pad="24px 0" bg2={getRoleBgColor(otherUser?.type)}>
          <Row centerAll wrapFlex noFlex pad="0 12px">
            <Avatar user={otherUser} showVerified />

            {/* MOBILE VIEW for LONG NAMES */}
            {isMobile && otherUser?.name.length > 20 ? (
              <Row marg="12px 0 0 0" noFlex>
                <Text h3 col={'#fff'} center>
                  {otherUser?.name}
                </Text>

                <Text c1 med col={'#fff'} center>
                  @{otherUser.username}
                </Text>
              </Row>
            ) : (
              <>
                <Col marg="0 0 0 12px" center wrap={+true}>
                  <Text h3 col={'#fff'} style={{ maxWidth: '325px' }}>
                    {otherUser?.name}
                  </Text>

                  <Text c1 med col={'#fff'}>
                    @{otherUser.username}
                  </Text>
                </Col>
              </>
            )}
          </Row>
        </Col>

        {/* Display LOGIN or Sign Up with SOCIALS/EMAIL */}
        {renderCurrentStep()}

        <Logo wid="156px" />
      </Col>
    );
  };

  return (
    // Anchor the wrapper at the parent's 100% width and forbid horizontal
    // overflow — without this a wide child section (many quiz cards, etc.)
    // makes this auto-width Col grow past the profile's 936px container.
    <Col
      wid="100%"
      gap={isMobile ? '24px' : '34px'}
      style={{ minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}
    >
      {loading && <LoadingOverlay />}
      <ShoutoutsRow
        marg="0"
        data={user.type === 'fan' ? user.sentShoutouts : user.shoutouts}
        title={t('profile.shoutouts')}
        contentLocked={contentLocked}
        handleUnlock={handleUnlock}
        handleSignup={() => {
          setShowSignUp(true); // Trigger the sign-up popup
        }}
      />

      {/* Polls + Quick Quiz on a team profile.
          - Managers (P4P admin / the channel owner viewing their own profile) keep the full
            management rows: QuickQuizRow + PollsRow with inline create/edit/delete.
          - Fans/athletes get the single rotating Team Channel slot (poll ↔ half-time quiz ↔
            coming soon), driven by /teamChannel/surface, plus the daily-quiz row below it.
          - Other manager accounts (a team admin viewing a channel they don't own) get
            nothing: they manage channels but never participate — only fans/athletes play. */}
      {user?.type === 'team' &&
        (canManagePollChannel(currentUser, user._id) ? (
          <>
            <QuickQuizRow team={user} />
            <PollsRow team={user} />
          </>
        ) : getPollRole(currentUser) ? null : (
          <>
            <TeamChannelSurface team={user} />
            <QuickQuizRow team={user} dailyOnly />
          </>
        ))}

      <PostsGrid
        data={user.posts}
        title={t('home.latestPosts')}
        contentLocked={contentLocked}
        handleUnlock={handleUnlock}
        showFour={!currentUser}
        onSeeAll={() => {
          setShowSignUp(true); // Trigger the sign-up popup
        }}
        handleSignup={() => {
          setShowSignUp(true); // Trigger the sign-up popup
        }}
        otherUser={otherUser}
        setOtherUser={setOtherUser}
      />

      {/* Conditionally render sign-up */}
      <Modal
        visible={showSignUp}
        closeModal={() => setShowSignUp(false)}
        wid={isMobile ? '325px !important ' : 'auto !important'}
        contentArea2MaxHt="84vh"
        closeIconFill={showSignUp ? '#fff' : theme.GREY_60}
      >
        <Col center wid="100%" pad="0 0 24px 0" marg="0 auto" boxSizing="border-box">
          {loadingSocialSignIn && <LoadingOverlay />}
          {renderSignUp()}
        </Col>
      </Modal>
    </Col>
  );
};

export default Fan;
